import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Loader2, AlertTriangle, Save, RotateCcw, Lock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CAT_LABELS = {
  luz_gas: 'Luz e Gás',
  telecomunicacoes: 'Telecomunicações',
  alarmes: 'Alarmes',
  solar: 'Solar',
  carregamento: 'Carregamento Elétrico',
};
const COMP_LABELS = {
  edp: 'EDP', galp: 'Galp', endesa: 'Endesa', iberdrola: 'Iberdrola',
  gold: 'Gold Energy', repsol: 'Repsol', yes: 'Yes', meo_energia: 'Meo Energia',
  nos: 'NOS', meo: 'MEO', vodafone: 'Vodafone',
  nos_securitas: 'NOS Securitas', prosegur: 'Prosegur',
};

const UNIT_DAYS = { dias: 1, meses: 30, anos: 365 };

const pickUnit = (days) => {
  const d = Number(days);
  if (!Number.isFinite(d) || d <= 0) return 'dias';
  if (d >= 365 && d % 365 === 0) return 'anos';
  if (d >= 30 && d % 30 === 0) return 'meses';
  return 'dias';
};

const daysToValueInUnit = (days, unit) => {
  const d = Number(days);
  if (!Number.isFinite(d)) return '';
  return String(Math.round(d / UNIT_DAYS[unit]));
};

const valueInUnitToDays = (value, unit) => {
  const v = parseInt(value, 10);
  if (Number.isNaN(v)) return null;
  return v * UNIT_DAYS[unit];
};

export const FidelizacaoRulesPage = () => {
  const { user } = useAuth();
  const [rules, setRules] = useState({});
  const [edited, setEdited] = useState({});
  const [units, setUnits] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const canEdit = user?.role === 'super_admin';

  useEffect(() => { fetchRules(); }, []);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/fidelizacao-rules`);
      const data = res.data.rules || {};
      setRules(data);
      const u = {};
      Object.entries(data).forEach(([cat, comps]) => {
        Object.entries(comps).forEach(([comp, info]) => {
          u[`${cat}::${comp}`] = pickUnit(info.days);
        });
      });
      setUnits(u);
      setEdited({});
    } catch {
      toast.error('Erro ao carregar regras de fidelização');
    } finally {
      setLoading(false);
    }
  };

  const setVal = (cat, comp, val) => {
    setEdited(prev => ({ ...prev, [`${cat}::${comp}`]: { value: val, unit: units[`${cat}::${comp}`] || 'dias' } }));
  };

  const setUnit = (cat, comp, newUnit) => {
    const k = `${cat}::${comp}`;
    const currentDays = edited[k]?.reset
      ? rules[cat]?.[comp]?.default
      : (edited[k]?.value !== undefined
          ? valueInUnitToDays(edited[k].value, edited[k].unit) ?? rules[cat]?.[comp]?.days
          : rules[cat]?.[comp]?.days);
    setUnits(prev => ({ ...prev, [k]: newUnit }));
    setEdited(prev => ({
      ...prev,
      [k]: { value: daysToValueInUnit(currentDays || 0, newUnit), unit: newUnit }
    }));
  };

  const getDisplayedValue = (cat, comp) => {
    const k = `${cat}::${comp}`;
    const e = edited[k];
    const unit = units[k] || 'dias';
    if (e?.reset) {
      return daysToValueInUnit(rules[cat]?.[comp]?.default ?? 0, unit);
    }
    if (e?.value !== undefined) return e.value;
    return daysToValueInUnit(rules[cat]?.[comp]?.days ?? 0, unit);
  };

  const reset = (cat, comp) => {
    const k = `${cat}::${comp}`;
    const defaultDays = rules[cat]?.[comp]?.default ?? 0;
    const newUnit = pickUnit(defaultDays);
    setUnits(prev => ({ ...prev, [k]: newUnit }));
    setEdited(prev => ({ ...prev, [k]: { reset: true, value: daysToValueInUnit(defaultDays, newUnit), unit: newUnit } }));
  };

  const buildPayload = () => {
    const out = {};
    Object.entries(edited).forEach(([k, e]) => {
      const [cat, comp] = k.split('::');
      const days = valueInUnitToDays(e.value, e.unit);
      if (days == null) return;
      if (!out[cat]) out[cat] = {};
      out[cat][comp] = days;
    });
    return out;
  };

  const save = async () => {
    const payload = buildPayload();
    if (Object.keys(payload).length === 0) {
      toast.info('Sem alterações para guardar');
      return;
    }
    try {
      setSaving(true);
      await axios.put(`${API_URL}/fidelizacao-rules`, { rules: payload });
      toast.success('Regras de fidelização atualizadas');
      await fetchRules();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = useMemo(() => Object.keys(edited).length > 0, [edited]);

  const formatDefault = (defaultDays) => {
    const d = Number(defaultDays) || 0;
    if (d === 0) return '0 dias';
    if (d % 365 === 0 && d >= 365) return `${d / 365} ano${d / 365 === 1 ? '' : 's'}`;
    if (d % 30 === 0 && d >= 30) return `${d / 30} ${d / 30 === 1 ? 'mês' : 'meses'}`;
    return `${d} dia${d === 1 ? '' : 's'}`;
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="fidelizacao-rules-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-outfit flex items-center gap-2">
              <Lock className="w-6 h-6 text-[#2D3E6F]" />
              Prazos de Fidelização (Cliente)
            </h1>
            <p className="text-slate-500 mt-1 text-sm">
              Define o <strong>período de fidelização do cliente</strong> por empresa — prazo em que o cliente fica <strong>vinculado ao fornecedor</strong>. Alimenta a página <em>Oportunidades de Troca</em> e o <em>Verificador Iberdrola</em>. Independente do chargeback.
            </p>
          </div>
          {hasChanges && canEdit && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={fetchRules} disabled={saving} data-testid="cancel-fid-rules-btn">Cancelar</Button>
              <Button onClick={save} disabled={saving} className="bg-[#2D3E6F] hover:bg-[#1a2a4a] gap-2" data-testid="save-fid-rules-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar alteracoes
              </Button>
            </div>
          )}
        </div>

        {!canEdit && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              Apenas o <strong>Super Admin</strong> pode editar estes prazos. Os campos abaixo são apenas de consulta.
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          Object.entries(rules).map(([cat, comps]) => (
            <Card key={cat} className="rounded-2xl border-slate-100" data-testid={`fid-rules-cat-${cat}`}>
              <CardContent className="p-5">
                <h2 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-indigo-400" />
                  {CAT_LABELS[cat] || cat}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(comps).map(([comp, info]) => {
                    const k = `${cat}::${comp}`;
                    const unit = units[k] || 'dias';
                    const isOv = info.is_override || (edited[k] !== undefined);
                    return (
                      <div key={comp} className={`flex items-center gap-2 p-3 rounded-lg border ${isOv ? 'border-indigo-300 bg-indigo-50/40' : 'border-slate-100'}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{COMP_LABELS[comp] || comp}</p>
                          <p className="text-xs text-slate-400">Default: {formatDefault(info.default)}</p>
                        </div>
                        <Input
                          type="number"
                          min={0}
                          value={getDisplayedValue(cat, comp)}
                          onChange={(e) => setVal(cat, comp, e.target.value)}
                          disabled={!canEdit}
                          className="w-20 h-9 text-sm font-mono"
                          data-testid={`fid-rule-${cat}-${comp}`}
                        />
                        <Select
                          value={unit}
                          onValueChange={(v) => setUnit(cat, comp, v)}
                          disabled={!canEdit}
                        >
                          <SelectTrigger className="w-[88px] h-9" data-testid={`fid-unit-${cat}-${comp}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="dias">Dias</SelectItem>
                            <SelectItem value="meses">Meses</SelectItem>
                            <SelectItem value="anos">Anos</SelectItem>
                          </SelectContent>
                        </Select>
                        {canEdit && info.is_override && (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => reset(cat, comp)}
                            title="Repor default"
                            className="h-8 w-8 text-slate-500 hover:text-slate-700 flex-shrink-0"
                          >
                            <RotateCcw className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </Layout>
  );
};

export default FidelizacaoRulesPage;
